"use client";

/**
 * Root error boundary — catches errors thrown in the root layout itself
 * (before the regular error.tsx can mount). Because the root layout has
 * blown up at this point, this component MUST render its own <html> and
 * <body> — it can't depend on layout.tsx or any header/footer that lives
 * inside it.
 *
 * This is the absolute last line of defence; under normal operation
 * route-level error.tsx handles everything.
 */

import { useEffect } from "react";

interface Props {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: Props) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error("[GlobalError]", error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          margin: 0,
          padding: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(at 20% 20%,#fee2e2,transparent 50%),radial-gradient(at 80% 0%,#fef3c7,transparent 50%),#fff",
          color: "#0b1233",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 560, padding: "24px" }}>
          <div
            style={{
              fontSize: "clamp(80px, 16vw, 140px)",
              fontWeight: 700,
              lineHeight: 0.85,
              marginBottom: 16,
              background: "linear-gradient(135deg,#0a1d36,#4a76a3)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Oops
          </div>
          <h1
            style={{
              fontSize: "clamp(24px, 4vw, 36px)",
              fontWeight: 600,
              margin: "0 0 14px",
            }}
          >
            Something hit a wave.
          </h1>
          <p
            style={{
              fontSize: 16,
              color: "#5a6478",
              lineHeight: 1.6,
              marginBottom: 28,
            }}
          >
            We hit a serious error. The issue has been logged — try reloading,
            or message us on WhatsApp and we&apos;ll sort it out fast.
          </p>
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={reset}
              style={{
                background: "#0e1e4d",
                color: "white",
                padding: "12px 24px",
                borderRadius: 9999,
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Try again
            </button>
            <a
              href="/"
              style={{
                background: "white",
                color: "#0b1233",
                padding: "12px 24px",
                borderRadius: 9999,
                fontWeight: 600,
                textDecoration: "none",
                border: "1px solid #e3e8ef",
                fontSize: 14,
              }}
            >
              Back to home
            </a>
            <a
              href="https://wa.me/201044414367"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: "#25d366",
                color: "white",
                padding: "12px 24px",
                borderRadius: 9999,
                fontWeight: 600,
                textDecoration: "none",
                fontSize: 14,
              }}
            >
              WhatsApp us
            </a>
          </div>
          {error.digest && (
            <p
              style={{
                fontSize: 11,
                color: "#5a6478",
                marginTop: 24,
                fontFamily: "monospace",
              }}
            >
              Reference: {error.digest}
            </p>
          )}
        </div>
      </body>
    </html>
  );
}
